import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { SortSelect, sortByField } from '../components/SortSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ONAY_RENKLERI = {
  beklemede: 'bg-yellow-900/50 text-yellow-300 border-yellow-700',
  onaylandi: 'bg-green-900/50 text-green-300 border-green-700',
  reddedildi: 'bg-red-900/50 text-red-300 border-red-700'
};
const ONAY_LABEL = { beklemede: 'Beklemede', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi' };

const SantiyeTakipPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [kontroller, setKontroller] = useState([]);
  const [ihaleler, setIhaleler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterDurum, setFilterDurum] = useState('all');
  const [filterTarih, setFilterTarih] = useState('');
  const [sortKey, setSortKey] = useState('newest');
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    ihale_id: '',
    tarih: new Date().toISOString().split('T')[0],
    giris_saati: '08:00',
    cikis_saati: '17:00',
    yapilan_is: '',
    personel_id: ''
  });

  useEffect(() => {
    if (!user?.is_admin && !user?.can_access_finance_module && !user?.is_viewer && user?.department !== 'yatirim_insaat') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/santiye-kontrol`, { withCredentials: true }),
        axios.get(`${API}/ihaleler`, { withCredentials: true })
      ];
      // Admin/admin yardımcısı ise kullanıcı listesini de al
      if (user?.is_admin || user?.is_admin_assistant) {
        promises.push(axios.get(`${API}/users-simple`, { withCredentials: true }));
      }
      const results = await Promise.all(promises);
      setKontroller(results[0].data);
      setIhaleler(results[1].data);
      if (results[2]) setUsers(results[2].data);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      ihale_id: '',
      tarih: new Date().toISOString().split('T')[0],
      giris_saati: '08:00',
      cikis_saati: '17:00',
      yapilan_is: '',
      personel_id: ''
    });
    setEditMode(false);
    setEditingRecord(null);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!formData.ihale_id) { alert('Lütfen bir ihale/şantiye seçin'); return; }
    setSubmitting(true);
    try {
      if (editMode && editingRecord) {
        await axios.put(`${API}/santiye-kontrol/${editingRecord.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/santiye-kontrol`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnay = async (id) => {
    try {
      await axios.put(`${API}/santiye-kontrol/${id}/onay`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert('Onay hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRed = async (id) => {
    if (!window.confirm('Bu kaydı reddetmek istediğinizden emin misiniz?')) return;
    try {
      await axios.put(`${API}/santiye-kontrol/${id}/red`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert('Red hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/santiye-kontrol/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert('Silme hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEditDialog = (record) => {
    setEditMode(true);
    setEditingRecord(record);
    setFormData({
      ihale_id: record.ihale_id,
      tarih: record.tarih,
      giris_saati: record.giris_saati,
      cikis_saati: record.cikis_saati,
      yapilan_is: record.yapilan_is || '',
      personel_id: record.personel_id || ''
    });
    setDialogOpen(true);
  };

  // Süre hesapla
  const hesaplaSure = (giris, cikis) => {
    if (!giris || !cikis) return '-';
    const [gh, gm] = giris.split(':').map(Number);
    const [ch, cm] = cikis.split(':').map(Number);
    const dakika = (ch * 60 + cm) - (gh * 60 + gm);
    if (dakika <= 0) return '-';
    const saat = Math.floor(dakika / 60);
    const dk = dakika % 60;
    return `${saat} sa ${dk > 0 ? dk + ' dk' : ''}`;
  };

  // Filtreleme
  const filteredKontroller = kontroller.filter(k => {
    if (filterDurum !== 'all' && k.onay_durumu !== filterDurum) return false;
    if (filterTarih && k.tarih !== filterTarih) return false;
    return true;
  });
  const SORT_DEFS_SANTIYE = {
    newest: { field: 'created_at', dir: -1 }, oldest: { field: 'created_at', dir: 1 },
    tarih_yeni: { field: 'tarih', dir: -1 }, tarih_eski: { field: 'tarih', dir: 1 }
  };
  const sortedKontroller = sortByField(filteredKontroller, sortKey, SORT_DEFS_SANTIYE);

  // İstatistikler
  const beklemede = kontroller.filter(k => k.onay_durumu === 'beklemede').length;
  const onaylanan = kontroller.filter(k => k.onay_durumu === 'onaylandi').length;
  const isAdmin = user?.is_admin || user?.is_admin_assistant;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Üst Menü */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">İhaleler Modülü</h1>
                <p className="text-xs text-gray-400">Şantiye Takip</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/finance/ihaleler')} variant="ghost" className="text-gray-300 hover:text-white">İhaleler</Button>
              <Button onClick={() => navigate('/finance/odenek')} variant="ghost" className="text-gray-300 hover:text-white">Ödenek</Button>
              <Button onClick={() => navigate('/finance/hakedis')} variant="ghost" className="text-gray-300 hover:text-white">Hakediş</Button>
              <Button variant="ghost" className="text-blue-400 border-b-2 border-blue-400">Şantiye Takip</Button>
              <Button onClick={async () => { await logout(); navigate('/login'); }} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Başlık */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Şantiye Kontrol Takibi</h2>
            <p className="text-gray-400 text-sm">Teknik personelin şantiye kontrol kayıtları</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="yeni-kontrol-btn">
            + Yeni Kontrol Kaydı
          </Button>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Toplam Kayıt</p>
              <p className="text-2xl font-bold text-white">{kontroller.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-yellow-800/50">
            <CardContent className="pt-4 pb-4">
              <p className="text-yellow-400 text-xs mb-1">Onay Bekleyen</p>
              <p className="text-2xl font-bold text-yellow-400">{beklemede}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-green-800/50">
            <CardContent className="pt-4 pb-4">
              <p className="text-green-400 text-xs mb-1">Onaylanan</p>
              <p className="text-2xl font-bold text-green-400">{onaylanan}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Bu Ay</p>
              <p className="text-2xl font-bold text-blue-400">{kontroller.filter(k => k.tarih?.startsWith(new Date().toISOString().slice(0, 7))).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtreler */}
        <Card className="bg-gray-900 border-gray-800 mb-4">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Durum:</span>
                {[{v:'all',l:'Tümü'},{v:'beklemede',l:'Beklemede'},{v:'onaylandi',l:'Onaylandı'},{v:'reddedildi',l:'Reddedildi'}].map(d => (
                  <Button key={d.v} size="sm" onClick={() => setFilterDurum(d.v)}
                    className={`h-7 px-3 text-xs ${filterDurum === d.v ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                  >{d.l}</Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Tarih:</span>
                <Input type="date" value={filterTarih} onChange={(e) => setFilterTarih(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white text-xs h-7 w-40" />
                {filterTarih && (
                  <Button size="sm" variant="ghost" onClick={() => setFilterTarih('')} className="text-red-400 h-7 text-xs">Temizle</Button>
                )}
              </div>
              <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
                { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
                { value: 'tarih_yeni', label: 'Tarih (Yeni→Eski)' }, { value: 'tarih_eski', label: 'Tarih (Eski→Yeni)' }
              ]} />
              <span className="text-xs text-gray-500 ml-auto">{sortedKontroller.length} kayıt</span>
            </div>
          </CardContent>
        </Card>

        {/* Tablo */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            {sortedKontroller.length === 0 ? (
              <p className="text-gray-400 text-center py-12">Kayıt bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="santiye-tablo">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Tarih</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Personel</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Şantiye (İhale)</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Giriş</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Çıkış</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Süre</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Yapılan İş</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">Durum</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-300">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {sortedKontroller.map(k => (
                      <tr key={k.id} className="hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                          {k.tarih ? new Date(k.tarih).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-300">{k.personel_adi}</td>
                        <td className="px-4 py-3">
                          <div className="text-gray-200 text-xs font-medium">{k.ihale_no}</div>
                          <div className="text-gray-500 text-xs truncate max-w-xs">{k.isin_adi}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{k.giris_saati}</td>
                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{k.cikis_saati}</td>
                        <td className="px-4 py-3 text-blue-400 whitespace-nowrap text-xs">{hesaplaSure(k.giris_saati, k.cikis_saati)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs max-w-xs truncate">{k.yapilan_is || '-'}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${ONAY_RENKLERI[k.onay_durumu] || ''}`}>
                            {ONAY_LABEL[k.onay_durumu] || k.onay_durumu}
                          </Badge>
                          {k.onaylayan_adi && (
                            <div className="text-xs text-gray-500 mt-1">{k.onaylayan_adi}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {/* Onay/Red butonları - sadece admin/birim şefi ve beklemede ise */}
                            {isAdmin && k.onay_durumu === 'beklemede' && (
                              <>
                                <Button size="sm" onClick={() => handleOnay(k.id)}
                                  className="h-7 px-2 text-xs bg-green-700 hover:bg-green-600 text-white" data-testid={`onay-${k.id}`}>
                                  Onayla
                                </Button>
                                <Button size="sm" onClick={() => handleRed(k.id)}
                                  className="h-7 px-2 text-xs bg-red-700 hover:bg-red-600 text-white" data-testid={`red-${k.id}`}>
                                  Reddet
                                </Button>
                              </>
                            )}
                            {/* Düzenle - kendi kaydı ve beklemede ise */}
                            {(user?.is_admin || k.personel_id === user?.id) && k.onay_durumu === 'beklemede' && (
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(k)}
                                className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300">
                                Düzenle
                              </Button>
                            )}
                            {/* Sil */}
                            {(user?.is_admin || k.personel_id === user?.id) && (
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(k.id)}
                                className="h-7 px-2 text-xs text-red-400 hover:text-red-300">
                                Sil
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Yeni Kayıt / Düzenle Diyalogu */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">{editMode ? 'Kontrol Kaydını Düzenle' : 'Yeni Şantiye Kontrol Kaydı'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Şantiye kontrol bilgilerini girin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* İhale/Şantiye Seçimi */}
            <div className="space-y-2">
              <Label className="text-white">Şantiye (İhale) *</Label>
              <Select value={formData.ihale_id} onValueChange={(v) => setFormData({...formData, ihale_id: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="İhale seçin..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                  {ihaleler.map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.ihale_no} - {i.isin_adi} ({i.ilcesi})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Personel Seçimi - Admin/Admin Yardımcısı için */}
            {(user?.is_admin || user?.is_admin_assistant) && (
              <div className="space-y-2">
                <Label className="text-white">Personel</Label>
                <Select value={formData.personel_id || 'self'} onValueChange={(v) => setFormData({...formData, personel_id: v === 'self' ? '' : v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Personel seçin..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                    <SelectItem value="self">Kendim ({user?.name})</SelectItem>
                    {users.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {u.unvan ? `(${u.unvan})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Boş bırakırsanız kendiniz adına kayıt oluşturulur</p>
              </div>
            )}

            {/* Tarih */}
            <div className="space-y-2">
              <Label className="text-white">Tarih *</Label>
              <Input type="date" value={formData.tarih}
                onChange={(e) => setFormData({...formData, tarih: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white" />
            </div>

            {/* Saat */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Giriş Saati *</Label>
                <Input type="time" value={formData.giris_saati}
                  onChange={(e) => setFormData({...formData, giris_saati: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Çıkış Saati *</Label>
                <Input type="time" value={formData.cikis_saati}
                  onChange={(e) => setFormData({...formData, cikis_saati: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>

            {/* Süre gösterimi */}
            {formData.giris_saati && formData.cikis_saati && (
              <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg px-3 py-2">
                <span className="text-blue-300 text-sm">Toplam süre: {hesaplaSure(formData.giris_saati, formData.cikis_saati)}</span>
              </div>
            )}

            {/* Yapılan İş Açıklaması */}
            <div className="space-y-2">
              <Label className="text-white">Yapılan İş / Kontrol Açıklaması</Label>
              <Textarea value={formData.yapilan_is}
                onChange={(e) => setFormData({...formData, yapilan_is: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white min-h-20"
                placeholder="Şantiyede yapılan kontrolleri ve gözlemleri yazın..." />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {submitting ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Kaydet')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SantiyeTakipPage;
